import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, inject } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { CoreModule } from './core.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { ToastsContainerComponent } from './shared/toasts-container/toasts-container.component';

import { RouterExtService } from './services/router-ext-service.service';
import { FirebaseApp, provideFirebaseApp, initializeApp } from '@angular/fire/app';
import {
  initializeFirestore,
  provideFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
} from '@angular/fire/firestore';
import { provideAuth, getAuth, connectAuthEmulator } from '@angular/fire/auth';
import {
  provideAnalytics,
  getAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { NewsBannerComponent } from './shared/news-banner/news-banner.component';
import { NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { Router, TitleStrategy } from '@angular/router';
import { MpactTitleStrategy } from './services/mpact-title-strategy.service';

@NgModule({ declarations: [AppComponent, ToastsContainerComponent, NewsBannerComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        CoreModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
        }),
        NgbToastModule], providers: [
        provideAnimationsAsync(),
        { provide: TitleStrategy, useClass: MpactTitleStrategy },
        RouterExtService,
        ScreenTrackingService,
        UserTrackingService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideFirestore(() => {
            const app = inject(FirebaseApp);
            const firestore = initializeFirestore(app, {
                localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
                // Suppresses noisy retry errors on Safari (and other browsers
                // with strict third-party storage / preflight handling) where
                // the Listen channel intermittently fails with
                // "Fetch API cannot load .../Listen/channel due to access
                // control checks." Firestore probes the transport on connect
                // and stays on the fallback path that survives these blocks.
                experimentalAutoDetectLongPolling: true,
            });
            if (environment.useEmulators.firestore) {
                connectFirestoreEmulator(firestore, environment.emulatorHost, 8080);
            }
            return firestore;
        }),
        provideAuth(() => {
            const auth = getAuth();
            if (environment.useEmulators.auth) {
                connectAuthEmulator(auth, `http://${environment.emulatorHost}:9099`);
            }
            return auth;
        }),
        provideAnalytics(() => getAnalytics()),
        provideFunctions(() => {
            const functions = getFunctions();
            if (environment.useEmulators.functions) {
                connectFunctionsEmulator(functions, environment.emulatorHost, 5001);
            }
            return functions;
        }),
    ] })
export class AppModule {}
